import { useEffect, useRef, type ReactNode } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import * as api from "./api/client";
import { useMe } from "./api/useMe";
import { LandingPage } from "./pages/LandingPage";
import { InDevelopmentPage } from "./pages/InDevelopmentPage";
import { LoginPage } from "./pages/LoginPage";
import { BannedPage } from "./pages/BannedPage";
import { ServerFullPage } from "./pages/ServerFullPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { CheckYourEmailPage } from "./pages/CheckYourEmailPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { ConvertAccountPage } from "./pages/ConvertAccountPage";
import { LobbyPage } from "./pages/LobbyPage";
import { BattlePage } from "./pages/BattlePage";
import { CollectionPage } from "./pages/CollectionPage";
import { DeckBuilderPage } from "./pages/DeckBuilderPage";
import { AdminPage } from "./pages/AdminPage";
import { NpcDeckEditorPage } from "./pages/NpcDeckEditorPage";
import { CardEditorPage } from "./pages/CardEditorPage";
import { PackOpeningPage } from "./pages/PackOpeningPage";
import { RoomPage } from "./pages/RoomPage";
import { WorldRoute } from "./pages/WorldRoute";
import { InventoryPage } from "./pages/InventoryPage";
import { useEmptyClickTap } from "./logic/useEmptyClickTap";
import { usePresenceConnect } from "./logic/usePresenceConnect";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LiveAnnouncerProvider } from "./logic/LiveAnnouncer";

// Wrap every route in an ErrorBoundary so a render-phase throw inside
// one page shows a visible error fallback instead of unmounting the
// React tree to a blank screen. The boundary's `label` becomes the
// fallback's "<X> crashed" heading.
function publicRoute(label: string, node: ReactNode) {
  return <ErrorBoundary label={label}>{node}</ErrorBoundary>;
}

// Every non-public route routes through `RequireAuth`, which:
//   - blocks paint until /api/me confirms a session,
//   - redirects logged-out users to /login (preserving the intended
//     destination via ?returnTo=),
//   - redirects banned users to /banned,
//   - redirects non-admins to /lobby when `admin: true`.
// Pages MUST NOT add their own auth-gate code — the guard is the
// single source of truth so we don't have to remember per-page.
function authedRoute(
  label: string,
  node: ReactNode,
  opts: { admin?: boolean } = {},
) {
  return (
    <ErrorBoundary label={label}>
      <RequireAuth requireAdmin={opts.admin}>{node}</RequireAuth>
    </ErrorBoundary>
  );
}

export function App() {
  useEmptyClickTap();
  usePresenceConnect();
  return (
    <BrowserRouter>
      <LiveAnnouncerProvider>
      <a href="#content" className="doi-skip-link">Skip to content</a>
      <BannedHandler />
      {/* `display: contents` keeps the <main> landmark for assistive
          tech while removing its layout box — pages own their own
          `height: 100%` chains and any block-level wrapper here
          would break them (the battle page in particular). */}
      <main id="content" tabIndex={-1} style={{ display: "contents" }}>
      <Routes>
        {/* Landing page is the new front door. No auth check —
            logged-in users see the same page; the "Play" CTA
            internally routes them on to `/lobby`. */}
        <Route path="/" element={publicRoute("Landing", <LandingPage />)} />
        {/* Alpha disclaimer, reached from the logo's "InDev!" splash.
            Public so logged-out visitors on the landing/login pages can
            read it too. */}
        <Route path="/in-development" element={publicRoute("In development", <InDevelopmentPage />)} />
        {/* Public auth surface — anyone can reach these. */}
        <Route path="/login" element={publicRoute("Login", <LoginPage />)} />
        <Route path="/register" element={publicRoute("Register", <RegisterPage />)} />
        <Route path="/forgot-password" element={publicRoute("Forgot password", <ForgotPasswordPage />)} />
        <Route path="/reset-password" element={publicRoute("Reset password", <ResetPasswordPage />)} />
        <Route path="/verify-email" element={publicRoute("Verify email", <VerifyEmailPage />)} />
        <Route path="/check-your-email" element={publicRoute("Check your email", <CheckYourEmailPage />)} />
        {/* /banned is its own kind: any user (banned or not, logged in
            or out) may render this URL, but a not-banned session
            bounces off it via BannedHandler. */}
        <Route path="/banned" element={publicRoute("Banned", <BannedPage />)} />
        {/* Lobby cap full. Reached when /api/login or /api/guest
            returns 409 capacity; the api/client capacity handler in
            BannedHandler routes here with ?queue=user|guest. */}
        <Route path="/full" element={publicRoute("Server full", <ServerFullPage />)} />

        {/* Authed surface — every page below is behind RequireAuth. */}
        <Route path="/account/change-password" element={authedRoute("Change password", <ChangePasswordPage />)} />
        <Route path="/convert" element={authedRoute("Account conversion", <ConvertAccountPage />)} />
        <Route path="/lobby" element={authedRoute("Lobby", <LobbyPage />)} />
        <Route path="/collection" element={authedRoute("Collection", <CollectionPage />)} />
        <Route path="/packs/:itemId" element={authedRoute("Pack opening", <PackOpeningPage />)} />
        <Route path="/inventory" element={authedRoute("Inventory", <InventoryPage />)} />
        <Route path="/decks/new" element={authedRoute("Deck builder", <DeckBuilderPage />)} />
        <Route path="/decks/:id/edit" element={authedRoute("Deck builder", <DeckBuilderPage />)} />
        <Route path="/battle/:gameId" element={authedRoute("Battle", <BattlePage />)} />
        <Route path="/room/:id" element={authedRoute("Room", <RoomPage />)} />
        <Route path="/world/*" element={authedRoute("World", <WorldRoute />)} />
        {/* Short invite URL — bounce to /room/:id so a freshly clicked
            link unwraps cleanly. RequireAuth on /room/:id then handles
            the logged-out case. */}
        <Route path="/r/:id" element={<RoomInviteRedirect />} />
        <Route path="/admin" element={<Navigate to="/admin/cards" replace />} />
        <Route
          path="/admin/npc-decks/:slug/edit"
          element={authedRoute("NPC deck editor", <NpcDeckEditorPage />, { admin: true })}
        />
        <Route
          path="/admin/card-editor/new"
          element={authedRoute("Card editor", <CardEditorPage />, { admin: true })}
        />
        <Route
          path="/admin/card-editor/:id/edit"
          element={authedRoute("Card editor", <CardEditorPage />, { admin: true })}
        />
        <Route
          path="/admin/:tab"
          element={authedRoute("Admin", <AdminPage />, { admin: true })}
        />
        <Route path="*" element={<Navigate to="/lobby" replace />} />
      </Routes>
      </main>
      </LiveAnnouncerProvider>
    </BrowserRouter>
  );
}

function RoomInviteRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/room/${id}`} replace />;
}

/**
 * Per-route auth gate. Paints from the cached `/api/me` user when one
 * exists (stale-while-revalidate — no blank frame on navigation), and
 * blocks paint only when the cache is cold (first load after a hard
 * refresh). Either way a fresh `/api/me` probe runs in the background
 * on every route change and routes the user appropriately:
 *
 *   - 401 → /login?returnTo=<current> (the probe also clears the me
 *     cache, so any optimistically-painted page unmounts)
 *   - banned → /banned
 *   - non-admin on an admin-only route → /lobby
 *   - otherwise → keep rendering children
 *
 * The trade: a user whose session expired (or whose admin bit was
 * dropped) since the cache was last warm can see one frame of stale
 * authed UI before the probe lands and redirects. The server stays
 * authoritative — any API call that page makes fails 401/403.
 *
 * All authed routes in `App.tsx` go through this. Pages MUST NOT
 * add their own per-page auth checks; that's the antipattern this
 * component exists to replace.
 */
export function RequireAuth({
  children,
  requireAdmin,
}: {
  children: ReactNode;
  requireAdmin?: boolean;
}): JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useMe();
  // Track the most recent probe so a fast navigation cascade doesn't
  // race itself into the wrong redirect.
  const generation = useRef(0);
  // Keep the latest location accessible to the probe without making
  // search-param changes (e.g. a filter input writing `?q=...`) cause
  // a re-probe — that would unmount the page on every keystroke.
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    const myGen = ++generation.current;
    (async () => {
      try {
        const me = await api.getMe();
        if (myGen !== generation.current) return;
        if (me.user.is_banned) {
          navigate("/banned", { replace: true });
          return;
        }
        if (requireAdmin && !me.user.is_admin) {
          navigate("/lobby", { replace: true });
        }
      } catch {
        if (myGen !== generation.current) return;
        // 401, network, unknown — treat all as auth failure to be
        // safe. getMe() already cleared the me cache on 401, which
        // unmounts any optimistically-painted children below.
        const here = locationRef.current;
        const ret = encodeURIComponent(here.pathname + here.search);
        navigate(`/login?returnTo=${ret}`, { replace: true });
      }
    })();
  }, [location.pathname, navigate, requireAdmin]);

  // Paint whenever the cached user satisfies the route's
  // requirements. Cold cache → null until the probe above populates
  // it (the `useMe` subscription re-renders us); warm cache → paint
  // immediately while the probe revalidates in the background.
  if (!user || user.is_banned || (requireAdmin && !user.is_admin)) {
    return null;
  }
  return <>{children}</>;
}

/**
 * Two responsibilities, kept together because they share the same
 * banned-state plumbing:
 *
 *   1. Register the api/client's `banned` 403 handler so any
 *      authed request that comes back as banned can route the SPA
 *      to /banned without each page handling it itself.
 *   2. When the user is sitting on `/banned` but the server says
 *      they're no longer banned (admin unbanned them), bounce to
 *      /lobby. When they're not logged in at all, bounce to /login.
 *
 * Mounted once at the top of <App> alongside the routes.
 */
function BannedHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.setBannedHandler(() => {
      navigate("/banned", { replace: true });
    });
    return () => api.setBannedHandler(null);
  }, [navigate]);

  useEffect(() => {
    api.setCapacityHandler(({ queue }) => {
      navigate(`/full?queue=${queue}`, { replace: true });
    });
    return () => api.setCapacityHandler(null);
  }, [navigate]);

  useEffect(() => {
    if (location.pathname !== "/banned") return;
    let cancelled = false;
    (async () => {
      try {
        const me = await api.getMe();
        if (cancelled) return;
        if (!me.user.is_banned) navigate("/lobby", { replace: true });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof api.ApiError && err.status === 401) {
          navigate("/login", { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, navigate]);

  return null;
}
